export interface SaveableFile {
  blob: Blob;
  filename: string;
}

type SaveFilesResult = 'saved' | 'cancelled' | 'unsupported';

interface SaveFilePickerOptions {
  suggestedName?: string;
  mode?: 'read' | 'readwrite';
}

interface WritableFileHandle {
  write: (data: Blob) => Promise<void>;
  close: () => Promise<void>;
}

interface DirectoryPickerWindow extends Window {
  showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<{
    createWritable: () => Promise<WritableFileHandle>;
  }>;
}

function supportsSaveFilePicker(): boolean {
  const directoryPickerWindow = window as DirectoryPickerWindow;

  return typeof directoryPickerWindow.showSaveFilePicker === 'function';
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export async function saveFilesToDirectory(files: SaveableFile[]): Promise<SaveFilesResult> {
  if (!supportsSaveFilePicker()) {
    return 'unsupported';
  }

  try {
    const directoryPickerWindow = window as DirectoryPickerWindow;

    for (const file of files) {
      const fileHandle = await directoryPickerWindow.showSaveFilePicker?.({
        mode: 'readwrite',
        suggestedName: file.filename,
      });

      if (fileHandle === undefined) {
        return 'unsupported';
      }

      const writable = await fileHandle.createWritable();

      await writable.write(file.blob);
      await writable.close();
    }

    return 'saved';
  } catch (error) {
    if (isAbortError(error)) {
      return 'cancelled';
    }

    throw error;
  }
}
